import { FormatCryptoCurrency } from '@/components/primitives';
import { UsdPrice } from '@/components/shared/UsdPrice/UsdPrice';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Flex,
  Text,
} from '@chakra-ui/react';
import { type ListModal } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { ComponentPropsWithoutRef } from 'react';

type Props = Parameters<
  Required<ComponentPropsWithoutRef<typeof ListModal>>['renderPriceBreakdown']
>[0];

export const IMXPriceBreakdown = ({
  marketplace,
  collection,
  price,
  quantity,
  currency,
  usdPrice,
}: Props) => {
  const { t } = useTranslation('common');
  const marketplaceFee = (marketplace?.fee.percent || 0) / 100;
  const royalty = (collection?.royalties?.bps || 0) / 100 / 100;
  const immutableFee = 2 / 100;
  const priceWithFees = Number(price) * (1 + marketplaceFee + royalty + immutableFee) * quantity;

  return (
    <Accordion w="full" allowToggle>
      <AccordionItem border="none" isDisabled={!price}>
        <AccordionButton justifyContent="space-between" p={0} _hover={{ opacity: 0.6 }}>
          <Flex align="center">
            <Text fontSize="lg" mr="space.4">
              {t('list-modal.total-price')}
            </Text>
            <AccordionIcon />
          </Flex>
          <Flex flexDir="column" align="flex-end">
            <FormatCryptoCurrency
              amount={priceWithFees}
              address={currency.contract}
              symbol={currency.symbol}
              maximumFractionDigits={2}
              fontSize="md"
              logoHeight={14}
            />
            {!!usdPrice && (
              <UsdPrice usdValue={priceWithFees * (usdPrice || 0)} fontSize="sm" color="gray.5" />
            )}
          </Flex>
        </AccordionButton>
        <AccordionPanel display="flex" flexDir="column" gap="space.4" p={0} pt="space.10">
          <BreakdownItem label={t('list-modal.listing-value')} amount={Number(price) * quantity} />
          {!!royalty && (
            <BreakdownItem
              label={t('list-modal.royalties')}
              amount={Number(price) * royalty * quantity}
            />
          )}
          <BreakdownItem
            label={t('list-modal.marketplace-fee')}
            amount={Number(price) * marketplaceFee * quantity}
          />
          <BreakdownItem
            label={t('list-modal.immutable-fee')}
            amount={Number(price) * immutableFee * quantity}
          />
          <Divider my="space.8" />
          <BreakdownItem label={t('list-modal.total-price')} amount={priceWithFees} />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const BreakdownItem = ({ label, amount }: { label: string; amount: number }) => (
  <Flex justify="space-between" color="gray.6">
    <Text>{label}</Text>
    <FormatCryptoCurrency amount={amount} logoHeight={10} symbol={label} />
  </Flex>
);
